import { useState } from "react";
import {
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type MultiFactorError,
  type MultiFactorResolver,
} from "firebase/auth";
import { auth } from "../services/firebase.service";
import { googleProvider } from "./googleProvider";

/**
 * Sign-in / sign-out actions for the portal, with the shared busy/error state
 * and MFA-pause handling. Kept separate from {@link useSsupSession}: this is the
 * *doing*, that hook is the *being signed in*.
 */
export interface SsupAuthActions {
  busy: boolean;
  error: string | null;
  /** Set only while a sign-in is paused on a second factor. */
  resolver: MultiFactorResolver | null;
  clearError: () => void;
  cancelMfa: () => void;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

export function useSsupAuthActions(): SsupAuthActions {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);

  const attempt = async (action: () => Promise<unknown>, fallback: string) => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (err) {
      if ((err as { code?: string }).code === "auth/multi-factor-auth-required") {
        setResolver(getMultiFactorResolver(auth, err as MultiFactorError));
      } else {
        setError(err instanceof Error ? err.message : fallback);
      }
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    error,
    resolver,
    clearError: () => setError(null),
    cancelMfa: () => setResolver(null),
    signInWithPassword: (email, password) =>
      attempt(() => signInWithEmailAndPassword(auth, email.trim(), password), "Sign-in failed"),
    signInWithGoogle: () =>
      attempt(() => signInWithPopup(auth, googleProvider), "Google sign-in failed"),
    signOutUser: async () => {
      setError(null);
      try {
        await signOut(auth);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Sign-out failed");
      }
    },
  };
}
